import { ComponentPropsWithoutRef, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { startGivebutterPlusFreeTrial } from 'api/subscriptions';
import * as Dialog from 'components/dialog';
import * as Form from 'components/form';
import { useRecommendedActionContext } from 'components/recommended-actions/RecommendedActionContext';
import { posthogEvents } from 'constants/posthog-events';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { usePosthog } from 'customHooks/usePosthog';
import GivebutterPlusLogo from 'images/givebutter-plus-logo.svg';
import GivebutterPlusSquiggle from 'images/givebutter-plus-squiggle.svg';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { twMerge } from 'tailwind-merge';
import { GivebutterAxiosError } from 'types/error';

interface GivebutterPlusTrialDialogFormValues {
    yearlyDonationVolume: string;
    estimatedContacts: string;
}

interface TrialFormProps extends ComponentPropsWithoutRef<'div'> {}
const TrialForm = ({ className, ...rest }: TrialFormProps) => {
    const toast = useToast();
    const { accountId, questionnaire } = useCurrentAccount();
    const { action } = useRecommendedActionContext();
    const { pushEvent } = usePosthog();

    const yearlyDonationVolumeDefault = useMemo(() => {
        if (!questionnaire.ntm_goal || isNaN(parseInt(questionnaire.ntm_goal))) {
            return '';
        }
        const goal = parseInt(questionnaire.ntm_goal);
        if (goal <= 10000) {
            return '10K';
        } else if (goal < 50000) {
            return '50K';
        } else if (goal <= 100000) {
            return '100K';
        } else {
            return '100K+';
        }
    }, [questionnaire.ntm_goal]);

    const estimatedContactsDefault = useMemo(() => {
        if (
            !questionnaire.number_of_contacts ||
            isNaN(parseInt(questionnaire.number_of_contacts))
        ) {
            return '';
        }
        const goal = parseInt(questionnaire.number_of_contacts);
        if (goal <= 1000) {
            return '1K';
        } else if (goal < 2500) {
            return '2.5K';
        } else if (goal < 5000) {
            return '5K';
        } else if (goal < 10000) {
            return '10K';
        } else {
            return '10K+';
        }
    }, [questionnaire.number_of_contacts]);

    const history = useHistory();

    const methods = Form.useGivebutterForm<GivebutterPlusTrialDialogFormValues>({
        defaultValues: {
            yearlyDonationVolume: yearlyDonationVolumeDefault,
            estimatedContacts: estimatedContactsDefault,
        },
    });

    const {
        control,
        path,
        resetFormButton,
        handleSubmit,
        setError,
        formState: { isValid },
    } = methods;

    const { mutate: submit, isPending: isLoading } = useMutation({
        mutationFn: (values: GivebutterPlusTrialDialogFormValues) =>
            startGivebutterPlusFreeTrial(accountId, {
                yearly_donation_volume: values.yearlyDonationVolume,
                estimated_contacts: values.estimatedContacts,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.current(accountId),
            });
            history.push('/complete');
            pushEvent(posthogEvents.DASHBOARD_RECOMMENDED_ACTION_PLUS_TRIAL_STARTED, {
                key: action.actionKey,
                title: action.title,
            });
        },
        onError: (error: GivebutterAxiosError) => {
            const data = error.response?.data;
            toast.error(data?.message ?? 'An unknown error occurred');
            resetFormButton();

            const errors = data?.errors;
            if (typeof errors === 'object') {
                for (let field in errors) {
                    setError(field as keyof GivebutterPlusTrialDialogFormValues, {
                        message: errors[field][0],
                    });
                }
            }
        },
    });

    return (
        <Form.Provider {...methods}>
            <form onSubmit={handleSubmit(values => submit(values))}>
                <div className={twMerge('flex w-full flex-col gap-8', className)} {...rest}>
                    <div className="flex w-full flex-col items-center gap-4">
                        <img
                            className={'w-full max-w-56'}
                            src={GivebutterPlusLogo}
                            alt="Givebutter Plus logo"
                        />
                        <div className="relative text-2xl">
                            <img
                                className="absolute -left-2.5 top-0 w-16"
                                src={GivebutterPlusSquiggle}
                                alt="Givebutter Plus squiggle"
                            />
                            <span className="font-medium">Free</span> for 30 days!
                        </div>
                        <div className="text-center text-base text-gray-600">
                            Please answer a few questions about your organization before starting
                            your free trial:
                        </div>
                    </div>
                    <div className="flex flex-col gap-6">
                        <Form.Container>
                            <Form.Label name={path('yearlyDonationVolume')}>
                                Yearly donation volume
                            </Form.Label>
                            <Form.SingleSelect
                                control={control}
                                name="yearlyDonationVolume"
                                rules={{
                                    required: 'Select your yearly donation volume',
                                }}
                            >
                                <SingleSelect.Trigger aria-label="Yearly donation volume" />
                                <SingleSelect.Content>
                                    <SingleSelect.Item value="10K">
                                        <SingleSelect.ItemText>$0 - $10,000</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="50K">
                                        <SingleSelect.ItemText>
                                            $10,001 - $49,000
                                        </SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="100K">
                                        <SingleSelect.ItemText>
                                            $50,000 - $100,000
                                        </SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="100K+">
                                        <SingleSelect.ItemText>$100,000 +</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                </SingleSelect.Content>
                            </Form.SingleSelect>
                            <Form.Error name={path('yearlyDonationVolume')} />
                        </Form.Container>

                        <Form.Container>
                            <Form.Label name={path('estimatedContacts')}>
                                Estimated contacts
                            </Form.Label>
                            <Form.SingleSelect
                                control={control}
                                name="estimatedContacts"
                                rules={{
                                    required: 'Select your estimated number of contacts',
                                }}
                            >
                                <SingleSelect.Trigger aria-label="Estimated contacts" />
                                <SingleSelect.Content>
                                    <SingleSelect.Item value="1K">
                                        <SingleSelect.ItemText>0 - 1,000</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="2.5K">
                                        <SingleSelect.ItemText>1,001 - 2,499</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="5K">
                                        <SingleSelect.ItemText>2,500 - 4,999</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="10K">
                                        <SingleSelect.ItemText>5,000 - 9,999</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="10K+">
                                        <SingleSelect.ItemText>10,000 +</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                </SingleSelect.Content>
                            </Form.SingleSelect>
                            <Form.Error name={path('estimatedContacts')} />
                        </Form.Container>
                    </div>
                    <div className="flex flex-col gap-3">
                        <Form.SubmitButton disabled={isLoading || !isValid} className="w-full">
                            Start trial
                        </Form.SubmitButton>
                        <Dialog.Cancel asChild>
                            <Button
                                variant="outline"
                                color="neutral"
                                onClick={() => history.push('/prompt')}
                            >
                                Not right now
                            </Button>
                        </Dialog.Cancel>
                    </div>
                </div>
            </form>
        </Form.Provider>
    );
};

export { TrialForm };
